import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext.js';

const ExperienceCenter = () => {
    const { theme, setTheme } = useTheme();
    const [behavior, setBehavior] = useState(() => {
        return localStorage.getItem('filterBehavior') || 'overlay';
    });
    const [tabMode, setTabMode] = useState(() => {
        return localStorage.getItem('tabViewMode') || 'hierarchical';
    });
    const [sidebarSubmenuMode, setSidebarSubmenuMode] = useState(() => {
        return localStorage.getItem('sidebarSubmenuMode') || 'groupwise';
    });

    useEffect(() => {
        // Ensure the body attribute matches current state
        document.body.setAttribute('data-filter-behavior', behavior);
    }, [behavior]);

    const handleSelectBehavior = (selected) => {
        setBehavior(selected);
        localStorage.setItem('filterBehavior', selected);
        document.body.setAttribute('data-filter-behavior', selected);
    };

    const handleSelectTabMode = (selected) => {
        setTabMode(selected);
        localStorage.setItem('tabViewMode', selected);
        window.dispatchEvent(new Event('tabViewModeChanged'));
    };

    const handleSelectSidebarSubmenuMode = (selected) => {
        setSidebarSubmenuMode(selected);
        localStorage.setItem('sidebarSubmenuMode', selected);
        window.dispatchEvent(new Event('sidebarSubmenuModeChanged'));
    };

    return (
        <div className="experience-center-container" style={{ padding: '24px', height: 'calc(100vh - 100px)', overflowY: 'auto' }}>
            <div className="settings-header" style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                    Experience Center
                </h2>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted, opacity: 0.7)', marginTop: '6px' }}>
                    Personalize your interface preferences, layouts, and workspace transitions.
                </p>
            </div>

            {/* SECTION 1: FILTER BEHAVIOR */}
            <div className="settings-section" style={{ marginBottom: '40px' }}>
                <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', marginBottom: '16px', borderBottom: '1px solid var(--grid-border-color)', paddingBottom: '8px' }}>
                    Filter Behavior
                </h3>
                <div className="settings-card-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', maxWidth: '960px' }}>
                    {/* Overlay Option Card */}
                    <div 
                        onClick={() => handleSelectBehavior('overlay')}
                        className={`pref-card ${behavior === 'overlay' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: behavior === 'overlay' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: behavior === 'overlay' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: behavior === 'overlay' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: behavior === 'overlay' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    Overlay Mode
                                </h4>
                            </div>
                            {behavior === 'overlay' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            Filters slide on top of the content with a blurred backdrop overlay. Keeps focus strictly on filter selections.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: 'var(--grid-header-color, rgba(0,0,0,0.05))', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid var(--grid-border-color)' 
                        }}>
                            <div style={{ width: '20%', height: '100%', borderRight: '1px solid var(--grid-border-color)', position: 'absolute', left: 0, top: 0 }} />
                            <div style={{ width: '80%', height: '100%', position: 'absolute', right: 0, top: 0, padding: '8px' }}>
                                <div style={{ width: '60%', height: '10px', background: 'var(--grid-border-color)', borderRadius: '4px', marginBottom: '6px' }} />
                                <div style={{ width: '90%', height: '6px', background: 'var(--grid-border-color)', borderRadius: '4px', opacity: 0.5 }} />
                            </div>
                            {/* Overlay backdrop */}
                            <div style={{ position: 'absolute', left: '20%', top: 0, width: '80%', height: '100%', background: 'rgba(15, 23, 42, 0.3)', backdropFilter: 'blur(1px)' }} />
                            {/* Drawer */}
                            <div style={{ width: '35%', height: '100%', background: 'var(--bg-color)', borderLeft: '1px solid var(--grid-border-color)', position: 'absolute', right: 0, top: 0, boxShadow: 'var(--shadow-sm)' }} />
                        </div>
                    </div>

                    {/* Push Menu Option Card */}
                    <div 
                        onClick={() => handleSelectBehavior('push')}
                        className={`pref-card ${behavior === 'push' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: behavior === 'push' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: behavior === 'push' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: behavior === 'push' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: behavior === 'push' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    Push Menu Mode
                                </h4>
                            </div>
                            {behavior === 'push' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            Filters push the main page content to the left to show the drawer. Keeps both page details and filters visible together.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: 'var(--grid-header-color, rgba(0,0,0,0.05))', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid var(--grid-border-color)' 
                        }}>
                            <div style={{ width: '20%', height: '100%', borderRight: '1px solid var(--grid-border-color)', position: 'absolute', left: 0, top: 0, zIndex: 2 }} />
                            {/* Pushed page content */}
                            <div style={{ width: '50%', height: '100%', position: 'absolute', left: '20%', top: 0, padding: '8px', borderRight: '1px solid var(--grid-border-color)', background: 'var(--grid-header-color)' }}>
                                <div style={{ width: '80%', height: '10px', background: 'var(--grid-border-color)', borderRadius: '4px', marginBottom: '6px' }} />
                                <div style={{ width: '90%', height: '6px', background: 'var(--grid-border-color)', borderRadius: '4px', opacity: 0.5 }} />
                            </div>
                            {/* Drawer pinned */}
                            <div style={{ width: '30%', height: '100%', background: 'var(--bg-color)', position: 'absolute', right: 0, top: 0 }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 2: THEME MODE */}
            <div className="settings-section" style={{ marginBottom: '40px' }}>
                <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', marginBottom: '16px', borderBottom: '1px solid var(--grid-border-color)', paddingBottom: '8px' }}>
                    Theme Mode
                </h3>
                <div className="settings-card-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', maxWidth: '960px' }}>
                    {/* Light Theme Card */}
                    <div 
                        onClick={() => setTheme('light')}
                        className={`pref-card ${theme === 'light' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: theme === 'light' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: theme === 'light' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: theme === 'light' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: theme === 'light' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    Light Theme
                                </h4>
                            </div>
                            {theme === 'light' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            Classic clean interface style. Best suited for well-lit workspaces and crisp contrast.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: '#FFFFFF', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid #E3E4E3' 
                        }}>
                            <div style={{ width: '20%', height: '100%', borderRight: '1px solid #E3E4E3', position: 'absolute', left: 0, top: 0, background: '#F8F8F8' }} />
                            <div style={{ width: '80%', height: '100%', position: 'absolute', right: 0, top: 0, padding: '8px', background: '#FFFFFF' }}>
                                <div style={{ width: '60%', height: '10px', background: '#E3E4E3', borderRadius: '4px', marginBottom: '6px' }} />
                                <div style={{ width: '90%', height: '6px', background: '#E3E4E3', borderRadius: '4px', opacity: 0.5 }} />
                                <div style={{ width: '40%', height: '20px', background: '#505258', borderRadius: '4px', marginTop: '20px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Dark Theme Card */}
                    <div 
                        onClick={() => setTheme('dark')}
                        className={`pref-card ${theme === 'dark' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: theme === 'dark' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: theme === 'dark' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: theme === 'dark' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: theme === 'dark' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    Dark Theme
                                </h4>
                            </div>
                            {theme === 'dark' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            Sleek slate dark interface style. Reduces eye strain and offers a beautiful, modern look.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: '#1F1F21', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid #2D2E30' 
                        }}>
                            <div style={{ width: '20%', height: '100%', borderRight: '1px solid #2D2E30', position: 'absolute', left: 0, top: 0, background: '#121214' }} />
                            <div style={{ width: '80%', height: '100%', position: 'absolute', right: 0, top: 0, padding: '8px', background: '#1F1F21' }}>
                                <div style={{ width: '60%', height: '10px', background: '#2D2E30', borderRadius: '4px', marginBottom: '6px' }} />
                                <div style={{ width: '90%', height: '6px', background: '#2D2E30', borderRadius: '4px', opacity: 0.5 }} />
                                <div style={{ width: '40%', height: '20px', background: '#38bdf8', borderRadius: '4px', marginTop: '20px' }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 3: TAB VIEW LAYOUT */}
            <div className="settings-section" style={{ marginBottom: '40px' }}>
                <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', marginBottom: '16px', borderBottom: '1px solid var(--grid-border-color)', paddingBottom: '8px' }}>
                    Tab View Layout
                </h3>
                <div className="settings-card-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', maxWidth: '960px' }}>
                    {/* Hierarchical View Card */}
                    <div 
                        onClick={() => handleSelectTabMode('hierarchical')}
                        className={`pref-card ${tabMode === 'hierarchical' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: tabMode === 'hierarchical' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: tabMode === 'hierarchical' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: tabMode === 'hierarchical' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: tabMode === 'hierarchical' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    Hierarchical View
                                </h4>
                            </div>
                            {tabMode === 'hierarchical' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            Sub-tabs are grouped under their main category tab. Sub-tabs appear as a hover dropdown menu.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: 'var(--grid-header-color, rgba(0,0,0,0.05))', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid var(--grid-border-color)' 
                        }}>
                            {/* main tab */}
                            <div style={{ display: 'flex', gap: '8px', padding: '8px', background: 'var(--bg-color)', borderBottom: '1px solid var(--grid-border-color)' }}>
                                <div style={{ width: '50px', height: '18px', background: 'hsl(var(--primary-hsl))', borderRadius: '4px' }} />
                                <div style={{ width: '40px', height: '18px', background: 'var(--grid-border-color)', borderRadius: '4px', opacity: 0.5 }} />
                            </div>
                            {/* hover dropdown */}
                            <div style={{ width: '80px', height: '50px', background: 'var(--bg-color)', border: '1px solid var(--grid-border-color)', borderRadius: '4px', position: 'absolute', left: '8px', top: '30px', boxShadow: 'var(--shadow-sm)', padding: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ width: '100%', height: '8px', background: 'var(--grid-border-color)' }} />
                                <div style={{ width: '80%', height: '8px', background: 'var(--grid-border-color)', opacity: 0.7 }} />
                                <div style={{ width: '90%', height: '8px', background: 'var(--grid-border-color)', opacity: 0.7 }} />
                            </div>
                        </div>
                    </div>

                    {/* Flat View Card */}
                    <div 
                        onClick={() => handleSelectTabMode('flat')}
                        className={`pref-card ${tabMode === 'flat' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: tabMode === 'flat' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: tabMode === 'flat' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: tabMode === 'flat' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: tabMode === 'flat' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    Flat View (One Line)
                                </h4>
                            </div>
                            {tabMode === 'flat' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            All open tabs and sub-tabs are displayed side-by-side in a single row. No nesting or hover dropdowns.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: 'var(--grid-header-color, rgba(0,0,0,0.05))', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid var(--grid-border-color)' 
                        }}>
                            {/* flat tabs */}
                            <div style={{ display: 'flex', gap: '6px', padding: '8px', background: 'var(--bg-color)', borderBottom: '1px solid var(--grid-border-color)', flexWrap: 'wrap' }}>
                                <div style={{ width: '40px', height: '18px', background: 'var(--grid-border-color)', borderRadius: '4px', opacity: 0.5 }} />
                                <div style={{ width: '55px', height: '18px', background: 'hsl(var(--primary-hsl))', borderRadius: '4px' }} />
                                <div style={{ width: '45px', height: '18px', background: 'var(--grid-border-color)', borderRadius: '4px', opacity: 0.5 }} />
                                <div style={{ width: '50px', height: '18px', background: 'var(--grid-border-color)', borderRadius: '4px', opacity: 0.5 }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 4: SIDEBAR SUBMENU LAYOUT */}
            <div className="settings-section" style={{ marginBottom: '40px' }}>
                <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', marginBottom: '16px', borderBottom: '1px solid var(--grid-border-color)', paddingBottom: '8px' }}>
                    Sidebar Submenu Layout
                </h3>
                <div className="settings-card-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', maxWidth: '960px' }}>
                    {/* Group-wise Submenu Card */}
                    <div 
                        onClick={() => handleSelectSidebarSubmenuMode('groupwise')}
                        className={`pref-card ${sidebarSubmenuMode === 'groupwise' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: sidebarSubmenuMode === 'groupwise' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: sidebarSubmenuMode === 'groupwise' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: sidebarSubmenuMode === 'groupwise' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: sidebarSubmenuMode === 'groupwise' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    Submenu Group-wise
                                </h4>
                            </div>
                            {sidebarSubmenuMode === 'groupwise' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            Level 3 submenus are nested under Level 2 items inside collapsible accordions.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: 'var(--grid-header-color, rgba(0,0,0,0.05))', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid var(--grid-border-color)',
                            padding: '8px'
                        }}>
                            {/* groupwise list */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-color)', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--grid-border-color)' }}>
                                    <div style={{ width: '60px', height: '8px', background: 'var(--text-color)', opacity: 0.8 }} />
                                    <div style={{ width: '6px', height: '6px', borderBottom: '1px solid var(--text-color)', borderRight: '1px solid var(--text-color)', transform: 'rotate(45deg)', opacity: 0.5 }} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '16px' }}>
                                    <div style={{ width: '70px', height: '6px', background: 'var(--text-color)', opacity: 0.4 }} />
                                    <div style={{ width: '50px', height: '6px', background: 'var(--text-color)', opacity: 0.4 }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Same Level Submenu Card */}
                    <div 
                        onClick={() => handleSelectSidebarSubmenuMode('sameLevel')}
                        className={`pref-card ${sidebarSubmenuMode === 'sameLevel' ? 'active' : ''}`}
                        style={{
                            background: 'var(--bg-card, rgba(255, 255, 255, 0.02))',
                            border: sidebarSubmenuMode === 'sameLevel' ? '2px solid hsl(var(--primary-hsl))' : '1px solid var(--grid-border-color)',
                            borderRadius: '12px',
                            padding: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: sidebarSubmenuMode === 'sameLevel' ? 'var(--shadow-md)' : 'var(--shadow-sm)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '8px', 
                                    background: sidebarSubmenuMode === 'sameLevel' ? 'hsla(var(--primary-hsl), 0.1)' : 'var(--grid-hover-bg-color)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <i className="icon-setting-outline" style={{ fontSize: '18px', color: sidebarSubmenuMode === 'sameLevel' ? 'hsl(var(--primary-hsl))' : 'var(--text-color)' }} />
                                </div>
                                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-color)', margin: 0 }}>
                                    On Same Level Submenu
                                </h4>
                            </div>
                            {sidebarSubmenuMode === 'sameLevel' && (
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'hsl(var(--primary-hsl))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="srkay-check" style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }} />
                                </div>
                            )}
                        </div>

                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color)', opacity: 0.7, lineHeight: 1.5, marginBottom: '20px', height: '40px' }}>
                            All clickable navigation links are listed flatly at the same indentation level in the sidebar panel.
                        </p>

                        {/* Miniature Visual Mockup */}
                        <div className="mini-mockup" style={{ 
                            height: '120px', 
                            background: 'var(--grid-header-color, rgba(0,0,0,0.05))', 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            position: 'relative', 
                            border: '1px solid var(--grid-border-color)',
                            padding: '8px'
                        }}>
                            {/* same level list */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-color)', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--grid-border-color)' }}>
                                    <div style={{ width: '70px', height: '8px', background: 'var(--text-color)', opacity: 0.8 }} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-color)', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--grid-border-color)' }}>
                                    <div style={{ width: '60px', height: '8px', background: 'var(--text-color)', opacity: 0.8 }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{__html: `
                .pref-card:hover {
                    transform: translateY(-2px);
                    border-color: hsla(var(--primary-hsl), 0.5) !important;
                    box-shadow: var(--shadow-md) !important;
                }
            `}} />
        </div>
    );
};

export default ExperienceCenter;
